import { ActivatedRoute, Data, Router, RouterLink } from '@angular/router';
import { AuthService, GeologicalSampleService, NotificationService } from '@services';
import { CardImageComponent } from '@components/card-image/card-image.component';
import { Component, effect, inject, signal, untracked } from '@angular/core';
import { environment } from '@environment';
import { HumanReadableSizePipe, MarkdownPipe } from '@pipes';
import { HttpParams, httpResource } from '@angular/common/http';
import { LeafletGroupComponent } from '@components/leaflet/group/group.component';
import { ListComponent } from '@components/list/list.component';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MatChipsModule } from '@angular/material/chips';
import { MatDialog } from '@angular/material/dialog';
import { MatGridListModule } from '@angular/material/grid-list';
import { MatIconModule } from '@angular/material/icon';
import { MatListModule } from '@angular/material/list';
import { MatMenuModule } from '@angular/material/menu';
import { MatPaginatorModule, PageEvent } from '@angular/material/paginator';

import {
  AttachmentListResponse,
  GeoJSONToCoordinates,
  GeologicalSample,
  Permission,
} from '@models';

@Component({
  selector: 'app-geology-sample-item',
  templateUrl: './item.component.html',
  styleUrls: ['./item.component.scss'],
  imports: [
    CardImageComponent,
    HumanReadableSizePipe,
    LeafletGroupComponent,
    ListComponent,
    MarkdownPipe,
    MatButtonModule,
    MatCardModule,
    MatChipsModule,
    MatGridListModule,
    MatIconModule,
    MatListModule,
    MatMenuModule,
    MatPaginatorModule,
    RouterLink,
  ],
})
export class GeologicalSampleItemComponent {
  // Router
  route = inject(ActivatedRoute);
  router = inject(Router);

  // Services
  authService = inject(AuthService);
  geologicalSampleService = inject(GeologicalSampleService);
  notificationService = inject(NotificationService);

  // Env vars
  kinko = environment.kinkoHost;
  geologicalSampleDatasetId = environment.geologicalSampleDatasetId;

  // Sample Data & Permissions
  sample = signal<GeologicalSample>(this.route.snapshot.data['sample']);
  coords = GeoJSONToCoordinates(
    this.sample().content?.geojson || {type:'FeatureCollection',features:[]}
  )[0] || [0,0,0];
  permissions = signal<Permission | null | undefined>(
      this.route.snapshot.data['permissions'],
  );

  // Dialog
  dialog = inject(MatDialog);

  // Attachment endpoint
  baseAddr = `${this.kinko}/dataset/${this.geologicalSampleDatasetId}/attachment/`

  // Image Paging
  defaultImgPageSize = 4;
  imgPage = signal(0);
  imgPageSize = signal(this.defaultImgPageSize);
  imgPageOptions = [this.defaultImgPageSize, 8, 24, 48];

  // Fetch images
  imgCount = signal(0);
  imgList = httpResource<AttachmentListResponse>(() => {
    const count = untracked(() => this.imgCount() === 0 || this.imgPage() === 0);
    const httpParams = new HttpParams()
      .set('skip', this.imgPage() * this.imgPageSize())
      .set('take', this.imgPageSize())
      .set('count', count)
      .set('recordId', this.sample().id)
      .set('prefix', '/sample/images/')
      .set('order', 'modified:desc');

    return `${this.baseAddr}?${httpParams.toString()}`;
  });

  onImgPage(event: PageEvent) {
    this.imgPage.set(event.pageIndex);
    this.imgPageSize.set(event.pageSize);
  }

  // File Paging
  defaultFilePageSize = 5;
  filePage = signal(0);
  filePageSize = signal(this.defaultFilePageSize);
  filePageOptions = [this.defaultFilePageSize, 20, 50, 100];

  // Fetch files
  fileCount = signal(0);
  fileList = httpResource<AttachmentListResponse>(() => {
    const count = untracked(() => this.fileCount() === 0 || this.filePage() === 0);
    const httpParams = new HttpParams()
      .set('skip', this.filePage() * this.filePageSize())
      .set('take', this.filePageSize())
      .set('count', count)
      .set('recordId', this.sample().id)
      .set('prefix', '/sample/docs/')
      .set('recursive', 'true')
      .set('order', 'modified:desc');

    return `${this.baseAddr}?${httpParams.toString()}`;
  });

  onFilePage(event: PageEvent) {
    this.filePage.set(event.pageIndex);
    this.filePageSize.set(event.pageSize);
  }

  constructor() {
    if (this.geologicalSampleDatasetId === '') {
      this.router.navigate(['/404'], {
        skipLocationChange: true,
      });
      this.notificationService.error('No geological sample dataset configured.');
    }

    // Effect that updates the image count signal
    effect(() => {
      if (!this.imgList.hasValue() || !this.imgList.value().count) {
        return;
      }

      this.imgCount.set(this.imgList.value().count);
    });

    // Effect that updates the file count signal
    effect(() => {
      if (!this.fileList.hasValue() || !this.fileList.value().count) {
        return;
      }

      this.fileCount.set(this.fileList.value().count);
    });

    this.route.data.subscribe((data: Data) => {
      this.sample.set(data['sample']);
      this.permissions.set(data['permissions']);
    });
  }
}
